import React, { useState } from "react";
import useUploadFiles from "../../../hooks/useUploadFiles";

// components
import Upload from "../../../component/modals/upload";
import TextBox from "../../../component/element/forms/TextBox";
import DateInput from "../../../component/element/forms/DateInput";

import TextInput from "../../../component/element/forms/TextInput";
import SelectOption from "../../../component/element/forms/SelectOption";

// state
import {
  create_opportunity,
  get_opportunities_type,
} from "../../../services/apis/opportunity";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  ICreateOpportunity,
  ICreateOpportunityError,
} from "../../../store/types/opportunity";
import Modal from "../../../component/ui/modal";
import DocumentUpload from "../../../component/modals/upload/DocumentUpload";
import { Image } from "lucide-react";
import { IErrorWarning } from "../../../store/types/misc";

type UploadedFile = {
  id: string;
  url: string;
  path: string;
};

type ICreateOpportunityType = {
  show: boolean;
  onHandler: () => void;
};

const CreateOpportunity: React.FC<ICreateOpportunityType> = ({
  show,
  onHandler,
}) => {
  const dispatch = useAppDispatch();
  const {
    loader: imgLoader,
    uploadFile: uploadImageFile,
    deleteFiles: deleteImageFiles,
  } = useUploadFiles();
  const { uploadFile: uploadDocFile, deleteFiles: deleteDocFiles } =
    useUploadFiles();
  const [imgPath, setImgPath] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const types = useAppSelector(
    ({ opportunity }) => opportunity.opportunity_types,
  );
  const loading = useAppSelector(({ opportunity }) => opportunity.loading);

  // const [loading] = useState(false);

  const [data, dataSet] = React.useState<ICreateOpportunity>({
    opportunity_name: "",
    opportunity_files: "",
    opportunity_description: "",
    expire_date: "",
    opportunity_image: "",
    opportunity_type: 0,
  });

  const [error, errorSet] = React.useState<ICreateOpportunityError>({
    opportunity_name: "",
    opportunity_files: "",
    opportunity_description: "",
    expire_date: "",
    opportunity_image: "",
    opportunity_type: "",
  });

  const clear = () => {
    dataSet({
      opportunity_name: "",
      opportunity_files: "",
      opportunity_description: "",
      expire_date: "",
      opportunity_image: "",
      opportunity_type: 0,
    });
    setUploadedFiles([]);
    setImgPath("");
    errorSet({
      opportunity_name: "",
      opportunity_files: "",
      opportunity_description: "",
      expire_date: "",
      opportunity_image: "",
      opportunity_type: "",
    });
  };

  const handleOk = async () => {
    const required: IErrorWarning = "error: this field is required";
    const errors: ICreateOpportunityError = {
      opportunity_name: data.opportunity_name.trim() ? "" : required,
      opportunity_files: data.opportunity_files.trim() ? "" : required,
      opportunity_description: data.opportunity_description.trim()
        ? ""
        : required,
      expire_date: data.expire_date.trim() ? "" : required,
      opportunity_image: data.opportunity_image ? "" : required,
      opportunity_type: data.opportunity_type ? "" : required,
    };
    errorSet(errors);
    if (Object.values(errors).some(Boolean)) return;
    const response = await create_opportunity(data, dispatch);
    if (response) {
      clear();
      onHandler();
    }
  };

  const onLoad = React.useCallback(() => {
    get_opportunities_type(dispatch);
  }, [dispatch]);

  React.useEffect(() => {
    onLoad();
  }, [onLoad]);

  return (
    <Modal
      title="Create Opportunity"
      open={show}
      onClose={() => {
        clear();
        onHandler();
      }}
      onOkClick={handleOk}
      isLoading={loading}
    >
      <div className="space-y-6">
        <div className="flex flex-col gap-4">
          <Upload
            icon={<Image />}
            title="Upload Image"
            label="JPG, PNG"
            name="opportunity_image"
            loading={imgLoader}
            status={error.opportunity_image}
            completed={Boolean(data.opportunity_image)}
            imgPreview={data.opportunity_image}
            onChange={async (fileOrFiles) => {
              const paths = await uploadImageFile(
                fileOrFiles as File,
                "opportunity_image",
              );

              if (paths.length > 0) {
                const img_path = paths[0].base_url + "/" + paths[0].path;
                dataSet((prev) => ({ ...prev, opportunity_image: img_path }));
                errorSet((prev) => ({ ...prev, opportunity_image: "" }));
                setImgPath(paths[0].path);
              }
            }}
            onDelete={async () => {
              if (imgPath) {
                const res = await deleteImageFiles(imgPath);
                if (res) {
                  dataSet((prev) => ({ ...prev, opportunity_image: "" }));
                }
              }
            }}
          />
          <DocumentUpload
            title="File Upload"
            name="opportunity_file"
            maxFiles={1}
            onChange={async (items) => {
              const paths = await uploadDocFile(
                items[0]?.file ?? null,
                "opportunity_file",
                "document",
              );
              if (paths.length > 0 && items[0]) {
                const uploaded = {
                  id: items[0].id,
                  url: paths[0].file_url,
                  path: paths[0].path,
                };
                setUploadedFiles([uploaded]);
                dataSet((prev) => ({
                  ...prev,
                  opportunity_files: uploaded.url,
                }));
                errorSet((prev) => ({ ...prev, opportunity_files: "" }));
              }
            }}
            onDelete={async (fileId) => {
              const fileToDelete = uploadedFiles.find(
                (file) => file.id === fileId,
              );
              if (!fileToDelete) {
                return;
              }

              const res = await deleteDocFiles(fileToDelete.path);

              if (res) {
                setUploadedFiles((prev) =>
                  prev.filter((file) => file.id !== fileId),
                );

                dataSet((prev) => ({ ...prev, opportunity_files: "" }));
              }
            }}
          />
          {error.opportunity_files && (
            <p className="text-xs text-red-500">
              Upload at least one opportunity document
            </p>
          )}
        </div>

        <TextInput
          label="Opportunity Name"
          name="opportunity_name"
          placeholder="Opportunity Name"
          type="text"
          value={data.opportunity_name}
          status={error.opportunity_name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const { name, value } = e.target;
            dataSet((prev) => ({ ...prev, [name]: value }));
            errorSet((prev) => ({ ...prev, opportunity_name: "" }));
          }}
        />

        <div className="inline-grid gap-6 w-full">
          <SelectOption
            items={types}
            label="Opportunity Type"
            placeholder="Select Type"
            value={data.opportunity_type ? String(data.opportunity_type) : ""}
            onChange={(selected) => {
              dataSet((prev) => ({
                ...prev,
                opportunity_type: selected ? Number(selected) : 0,
              }));
              errorSet((prev) => ({ ...prev, opportunity_type: "" }));
            }}
          />
          {error.opportunity_type && (
            <p className="text-xs text-red-500">Select an opportunity type</p>
          )}

          <DateInput
            label="Select Expire Date"
            error={
              error.expire_date ? "Expiration date is required" : undefined
            }
            onChange={(date) => {
              dataSet((prev) => ({
                ...prev,
                expire_date: date.split(" ")?.[0],
              }));
              errorSet((prev) => ({ ...prev, expire_date: "" }));
            }}
          />
        </div>

        <TextBox
          label="Opportunity Description"
          name="opportunity_description"
          value={data.opportunity_description}
          placeholder="Opportunity Description"
          onChange={(name, value) => {
            dataSet((prev) => ({ ...prev, [name]: value }));
            errorSet((prev) => ({ ...prev, opportunity_description: "" }));
          }}
        />
        {error.opportunity_description && (
          <p className="text-xs text-red-500">Enter a description</p>
        )}
      </div>
    </Modal>
  );
};

export default CreateOpportunity;
